// API response types for the summoner lookup site

export interface ApiResponse<T> {
	success: boolean;
	data?: T;
	error?: string;
	retryAfterSeconds?: number;
	cooldownRemaining?: number;
	needsTurnstile?: boolean;
}

export interface PlayerRank {
	tier: string;
	rank: string;
	leaguePoints: number;
	wins: number;
	losses: number;
}

export interface Summoner {
	name: string;
	tagLine: string;
	/** URL slug: euw, na, kr, ... */
	region: string;
	platform: string;
	profileIconId: number;
	summonerLevel: number | null;
}

export interface Ranks {
	solo: PlayerRank | null;
	flex: PlayerRank | null;
	/** Ranked 5s (queue 710), weekend-only */
	premade: PlayerRank | null;
}

/** One rank snapshot for the LP graph. `points` is a single ladder number (division = 100, tier = 400). */
export interface LpPoint {
	t: number;
	points: number;
	tier: string;
	rank: string;
	lp: number;
}

export interface LpHistory {
	solo: LpPoint[];
	flex: LpPoint[];
}

export interface MatchHistory {
	id: number;
	matchId: string;
	championId: number;
	championName: string;
	win: boolean;
	isRemake: boolean;
	/** LP change of this ranked game; null when it could not be pinned on this game (see worker services/lp.ts) */
	lpDelta: number | null;
	kills: number;
	deaths: number;
	assists: number;
	goldEarned: number;
	totalMinionsKilled: number;
	neutralMinionsKilled: number;
	visionScore: number;
	teamPosition: string | null;
	summoner1Id: number | null;
	summoner2Id: number | null;
	primaryKeystone: number | null;
	secondaryRuneStyle: number | null;
	timestamp: string;
	gameMode: string;
	queueId: number;
	/** Riot build the game ran on, e.g. "16.1.612.4890": icons are pinned to this patch */
	gameVersion: string;
	gameDuration: number;
	// Items (0-5 are regular items, 6 is trinket/ward)
	item0: number;
	item1: number;
	item2: number;
	item3: number;
	item4: number;
	item5: number;
	item6: number;
}

export interface ChampionStats {
	championId: number;
	championName: string;
	gamesPlayed: number;
	wins: number;
	losses: number;
	winrate: number;
	avgKills: number;
	avgDeaths: number;
	avgAssists: number;
	kda: number;
	csPerMin: number;
}

export interface Highlights {
	games: number;
	wins: number;
	losses: number;
	multiKills: { pentas: number; quadras: number; triples: number };
	currentStreak: { type: "win" | "loss"; count: number };
	avgTeamDamagePercent: number | null;
}

export interface Status {
	/** Riot was busy, so this is the last data we have */
	stale: boolean;
	retryAfterSeconds?: number;
	/** more games are still being fetched in the background */
	refreshing: boolean;
	updatedAt: number | null;
}

export interface SummonerProfile {
	summoner: Summoner;
	ranks: Ranks;
	lpHistory: LpHistory;
	games: MatchHistory[];
	hasMore: boolean;
	champions: ChampionStats[];
	highlights: Highlights;
	status: Status;
}

export interface GamesPage {
	games: MatchHistory[];
	hasMore: boolean;
	status: Status;
}

export interface Suggestion {
	name: string;
	tagLine: string;
	region: string;
	profileIconId: number;
	summonerLevel: number | null;
}

// ---- match scoreboard ----

export interface ScoreboardPlayer {
	name: string;
	tagLine: string;
	championId: number;
	championName: string;
	level: number;
	position: string;
	kills: number;
	deaths: number;
	assists: number;
	cs: number;
	gold: number;
	damage: number;
	damageShare: number;
	damageTaken: number;
	visionScore: number;
	items: number[];
	summoner1Id: number;
	summoner2Id: number;
	keystone: number;
	primaryStyle: number;
	secondaryStyle: number;
	win: boolean;
}

export interface ScoreboardTeam {
	teamId: number;
	win: boolean;
	kills: number;
	deaths: number;
	assists: number;
	gold: number;
	objectives: { dragons: number; barons: number; towers: number; inhibitors: number; heralds: number; grubs: number };
	bans: number[];
	players: ScoreboardPlayer[];
}

export interface Scoreboard {
	matchId: string;
	queueId: number;
	gameMode: string;
	gameVersion: string;
	gameCreation: number;
	gameDuration: number;
	teams: ScoreboardTeam[];
}

// ---- live game ----

export interface ActiveGamePlayer {
	/** client-side key only (the server never sends puuids); used for role assignment */
	puuid: string;
	summonerName: string;
	teamId: number;
	championId: number;
	spell1Id: number;
	spell2Id: number;
	perks: {
		perkIds: number[];
		perkStyle: number;
		perkSubStyle: number;
	};
	rank: PlayerRank | null;
	summonerLevel: number | null;
	/** the summoner whose page this is */
	isTracked: boolean;
}

export interface ActiveGame {
	gameId: string;
	gameMode: string;
	gameType: string;
	gameStartTime: number;
	gameLength: number;
	platformId: string;
	participants: ActiveGamePlayer[];
}

// Rank tier order for sorting and styling
export const TIER_ORDER = [
	"IRON",
	"BRONZE",
	"SILVER",
	"GOLD",
	"PLATINUM",
	"EMERALD",
	"DIAMOND",
	"MASTER",
	"GRANDMASTER",
	"CHALLENGER"
] as const;

export type Tier = (typeof TIER_ORDER)[number];

// Rank division order
export const RANK_ORDER = ["IV", "III", "II", "I"] as const;
export type Rank = (typeof RANK_ORDER)[number];

// Tier colors for styling
export const TIER_COLORS: Record<string, { primary: string; secondary: string; glow: string }> = {
	IRON: { primary: "#6b6b6b", secondary: "#4a4a4a", glow: "rgba(107, 107, 107, 0.4)" },
	BRONZE: { primary: "#cd7f32", secondary: "#8b5a2b", glow: "rgba(205, 127, 50, 0.4)" },
	SILVER: { primary: "#c0c0c0", secondary: "#808080", glow: "rgba(192, 192, 192, 0.4)" },
	GOLD: { primary: "#ffd700", secondary: "#daa520", glow: "rgba(255, 215, 0, 0.4)" },
	PLATINUM: { primary: "#00cec9", secondary: "#00a8a3", glow: "rgba(0, 206, 201, 0.4)" },
	EMERALD: { primary: "#2ecc71", secondary: "#27ae60", glow: "rgba(46, 204, 113, 0.4)" },
	DIAMOND: { primary: "#b9f2ff", secondary: "#89cff0", glow: "rgba(185, 242, 255, 0.4)" },
	MASTER: { primary: "#9b59b6", secondary: "#8e44ad", glow: "rgba(155, 89, 182, 0.4)" },
	GRANDMASTER: { primary: "#e74c3c", secondary: "#c0392b", glow: "rgba(231, 76, 60, 0.4)" },
	CHALLENGER: { primary: "#f1c40f", secondary: "#f39c12", glow: "rgba(241, 196, 15, 0.5)" }
};

// ---- team scouting ----

export interface ScoutChampion {
	championId: number;
	championName: string;
	games: number;
	wins: number;
	kda: number;
	/** how many of those were team games */
	teamGames: number;
	/** position it was played on most */
	role?: string | null;
	lastPlayed?: number;
	/** % of the player's games */
	share?: number;
	score?: number;
}

// Fields marked optional are missing in shared reports saved before the matchup report existed.
export interface ScoutPlayer {
	riotId: string;
	rank: PlayerRank | null;
	/** TOP | JUNGLE | MIDDLE | BOTTOM | UTILITY */
	role: string | null;
	/** position in the likely line-up; null = stand-in */
	startRole?: string | null;
	games: number;
	teamGames?: number;
	kda?: number;
	csPerMin?: number;
	dmgPerMin?: number;
	goldPerMin?: number;
	visionPerMin?: number;
	/** champions that cover 70% of their games */
	poolDepth?: number;
	/** % of the roster's damage / gold in team games */
	dmgShare?: number;
	goldShare?: number;
	champions: ScoutChampion[];
	/** the champions they met in their lane, most met first: their record against each */
	laneVs?: ScoutChampRecord[];
}

export type ScoutChampRecord = { championId: number; games: number; wins: number };

export interface ScoutTeamGame {
	matchId: string;
	kind: "tournament" | "flex" | "clash";
	win: boolean;
	side: "blue" | "red";
	duration: number;
	date: number;
	picks: { riotId: string; role: string; championId: number; championName: string }[];
	bans: number[];
	bansAgainst: number[];
}

/** One champion of the draft plan after server-side validation: a real champion from the candidates, a player on a roster. */
export interface DraftRec {
	championId: number;
	reason: string;
	player: string | null;
}

export interface ScoutReport {
	/** false: the model wrote nothing, the plan comes from the numbers alone */
	ai?: boolean;
	overview: string[];
	/** draft plan in tournament-draft order: 3 bans, their 3 likely picks, 2 bans, their 2 likely picks */
	bans1: DraftRec[];
	picks1: DraftRec[];
	bans2: DraftRec[];
	picks2: DraftRec[];
	players: { riotId: string; note: string; championIds: number[] }[];
	draft: string[];
	playstyle: string[];
	gamePlan: string[];
	winConditions?: string[];
	/** our side of the draft: only with our own roster */
	theirBans?: DraftRec[];
	ourPicks?: DraftRec[];
	firstPick?: DraftRec | null;
	lanes?: { role: string; note: string }[];
	blueSide?: string[];
	redSide?: string[];
	/** the model's take on our comp */
	comp?: string[];
	/** from champion traits: how our picks play and what they lack */
	compCheck?: string[];
	/** the same for the picks we expect from them */
	theirComp?: string[];
}

type ScoutTally = { championId: number; count: number }[];
type ScoutRecord = { games: number; wins: number };

export interface ScoutStats {
	players: ScoutPlayer[];
	team: {
		games: number;
		wins: number;
		tournamentGames: number;
		blue: ScoutRecord;
		red: ScoutRecord;
		avgDuration: number;
		winDuration?: number;
		lossDuration?: number;
		/** record by game length: under 25 min, 25-35, over 35 */
		byLength?: { short: ScoutRecord; mid: ScoutRecord; long: ScoutRecord };
		first: { blood: number; tower: number; dragon: number; baron: number; herald?: number; grubs?: number; atakhan?: number };
		damage?: { physical: number; magic: number };
		bans: ScoutTally;
		bansAgainst: ScoutTally;
		/** their bans by phase in tournament draft */
		bans1?: ScoutTally;
		bans2?: ScoutTally;
		flex?: { championId: number; players: string[]; roles: string[] }[];
		recent: ScoutTeamGame[];
		/** champion pairs two of them play on one side */
		duos?: { players: [string, string]; championIds: [number, number]; games: number; wins: number }[];
		/** enemy champions they lose to in team games */
		losesTo?: ScoutChampRecord[];
		/** comp styles of their team games */
		styles?: { style: string; games: number }[];
	};
}

export interface ScoutCandidate extends DraftRec {
	score: number;
}

export interface ScoutMatchup {
	lanes: { role: string; theirs: string | null; ours: string | null; contested: number[] }[];
	contested: { championId: number; theirs: string; ours: string }[];
	banTargets: ScoutCandidate[];
	theirBans: ScoutCandidate[];
	picks: ScoutCandidate[];
	side: { recommended: "blue" | "red" | null; reason: string };
	/** high-elo duo win rates among our pick candidates */
	synergies?: { roles: string; players: [string, string]; championIds: [number, number]; games: number; wins: number }[];
}

/** High-elo counters to one of their starter's main champions; player = our starter who plays the counter. */
export interface ScoutCounter {
	role: string;
	riotId: string;
	championId: number;
	counters: (ScoutChampRecord & { player: string | null })[];
}

// ---- tier list ----

export interface TierChampion {
	championId: number;
	games: number;
	wins: number;
	/** % of the lane's games */
	pickRate: number;
	tier: "S" | "A" | "B" | "C" | "D";
}

export interface TierList {
	/** region slug of the crawled ladder */
	region: string;
	/** newest first */
	patches: string[];
	games: number;
	/** by lane: TOP | JUNGLE | MIDDLE | BOTTOM | UTILITY, best first */
	roles: Record<string, TierChampion[]>;
}

export interface ProEvent {
	/** LoL Esports tournament id */
	id: string;
	name: string;
	games: number;
}

/** One pro tournament (LoL Esports): `games` = picks there, ranked by picks then wins. */
export interface ProTierList {
	/** tournament id */
	event: string;
	games: number;
	/** major-league tournaments of the last year with games, newest first */
	events: ProEvent[];
	roles: Record<string, TierChampion[]>;
}

export interface ScoutResult extends ScoutStats {
	/** our own team, when its roster was entered */
	own?: ScoutStats | null;
	matchup?: ScoutMatchup | null;
	counters?: ScoutCounter[];
	/** null when there was nothing to write a plan from */
	report: ScoutReport | null;
	generatedAt: number;
	/** region slug the roster was scouted on */
	region: string;
	/** share id: /scout/<id> shows this exact result again. null when the model wrote nothing */
	id: string | null;
}

/** One player's line in a tournament game (POST /api/analysis). */
export interface AnalysisLine {
	riotId: string;
	role: string;
	championId: number;
	championName: string;
	kills: number;
	deaths: number;
	assists: number;
	cs: number;
	/** CS and gold at 15:00 from the Riot timeline; missing for PL-only games, failed timelines and games under 15 min */
	at15?: { cs: number; gold: number };
	/** deaths before 10:00; missing without a timeline */
	deaths10?: number;
}
export interface AnalysisSide {
	side: "blue" | "red";
	win: boolean;
	kills: number;
	gold: number;
	/** top to support */
	players: AnalysisLine[];
	/** turn = global draft turn: phase 1 is 1-6, phase 2 is 7-10. Empty for blind-lobby games. */
	bans: { championId: number; turn: number }[];
	styles: string[];
}
export interface AnalysisGame {
	matchId: string;
	date: number;
	duration: number;
	/** true = in-client tournament draft; false = blind lobby after a drafter.lol draft (no bans in Riot's data) */
	draft: boolean;
	/** all 20 draft actions in order: only known from a pasted Prime League match page */
	order?: { kind: "ban" | "pick"; side: "blue" | "red"; championId: number }[];
	us: AnalysisSide;
	them: AnalysisSide;
	/** from the Riot timeline (worker services/analysis.ts); missing for PL-only games and unreadable timelines */
	early?: AnalysisEarly;
}
/** One game's early game from the team's side (`us`). */
export interface AnalysisEarly {
	/** team gold minus the opponent's, at the minutes the game reached */
	goldDiff: { at10?: number; at15?: number; at20?: number };
	/** where the team's jungler is at 2:00: the top or bottom half of the map */
	jungleStart?: "top" | "bot";
	/** the team's first kill on an enemy laner with its jungler in it, 1:30-10:00; lane = the victim's role */
	firstGank?: { lane: string; t: number };
	/** the team's first death before 15:00; ganked = the enemy jungler took part */
	firstDeath?: { riotId: string; role: string; t: number; ganked: boolean };
	/** a kill before 1:30 (a level 1 fight): did the team get it or give it up */
	lvl1?: "won" | "lost";
	/** the team's first epic monster: DRAGON, HORDE (grubs), RIFTHERALD, ... */
	firstObjective?: string;
	/** the game's first dragon, whoever took it */
	firstDragon?: { t: number; ours: boolean };
	/** the team's jungler at minutes 1-8, map coordinates (0-14800, y up) */
	junglePath?: [number, number][];
	/** the jungler's minutes 2-14: in the top or bottom half of the map, and in the enemy's jungle */
	jungleTime?: { minutes: number; top: number; bot: number; invading: number };
	/** the team's deaths before 15:00 and where they happened */
	deaths: { role: string; t: number; x: number; y: number }[];
	/** the bot laner was in top lane for 2+ of minutes 2-6 */
	laneSwap: boolean;
	/** minutes 3-14 the mid laner and support spent away from their lane and base; first = the first such minute */
	roams: { role: string; minutes: number; first?: number }[];
}
export interface AnalysisResult extends ScoutStats {
	games: AnalysisGame[];
	region: string;
	since: number;
	generatedAt: number;
}

/** A Prime League match page as GET /api/pl/matches/:id reads it (worker services/primeleague.ts). */
export interface PlDraftStep {
	kind: "ban" | "pick";
	side: "blue" | "red";
	champion: string;
}
export interface PlTeam {
	tag: string;
	win: boolean;
	/** top to support */
	players: { riotId: string; champion: string; kills: number; deaths: number; assists: number; cs: number }[];
}
export interface PlGame {
	/** Riot match id */
	matchId: string;
	start: number;
	duration: number;
	draft: PlDraftStep[];
	blue: PlTeam;
	red: PlTeam;
}
export interface PlMatch {
	id: number;
	/** tag -> full team name */
	teams: Record<string, string>;
	games: PlGame[];
}

export type LeaderboardRange = "1d" | "7d" | "30d";

/** One queue's change over the chosen range. `lp` is null when unranked at either end. */
export interface RangeChange {
	lp: number | null;
	wins: number;
	losses: number;
}

/** One of a member's newest ranked games (no remakes), newest first. */
export interface FormGame {
	matchId: string;
	win: boolean;
	queueId: number;
	championId: number;
	lpDelta: number | null;
	gameCreation: number;
}

export interface LeaderboardMember {
	/** stable id for removing the member; also what groups and feed events point at */
	id: number;
	summoner: Summoner;
	solo: PlayerRank | null;
	flex: PlayerRank | null;
	/** Ranked 5s */
	premade: PlayerRank | null;
	change: { solo: RangeChange | null; flex: RangeChange | null; premade: RangeChange | null };
	form: FormGame[];
	/** +n = n wins in a row, -n = n losses */
	streak: number;
}

/** Board members who played on the same team in the range, as an exact group (member ids): 2 in Solo/Duo, up to 5 in Flex / Ranked 5s. */
export interface LeaderboardGroup {
	ids: number[];
	games: number;
	wins: number;
}

/** Rank moves and pentakills, newest first. */
export type LeaderboardEvent =
	| { t: number; playerId: number; kind: "promoted" | "demoted" | "placed"; queue: "solo" | "flex" | "premade"; tier: string; rank: string }
	| { t: number; playerId: number; kind: "pentakill"; championId: number; matchId: string };

/** A board member in a game right now (Spectator). gameStartTime 0 = still loading in. */
export interface LeaderboardLiveGame {
	playerId: number;
	championId: number;
	queueId: number;
	gameStartTime: number;
}

export interface Leaderboard {
	name: string;
	slug: string;
	/** true when the request carried this board's edit key */
	editable: boolean;
	maxMembers: number;
	range: LeaderboardRange;
	members: LeaderboardMember[];
	groups: { solo: LeaderboardGroup[]; flex: LeaderboardGroup[]; premade: LeaderboardGroup[] };
	feed: LeaderboardEvent[];
}
